/**
 * The literal face copy, pinned by FABLE.
 *
 * TWO PARTS PER STATE, since 21 September 2026: a title and a body. The control used to be a bare
 * button with one sentence beside it, which read as an orphan next to the rest of the page. It is
 * now the same shape as the guest call to action above it, and that shape has a heading. The title
 * is what somebody reads first, the body is the state, and the button label is a third thing again
 * and never a repeat of either.
 *
 * The off title and body are FABLE set B, released by Mark on 21 September. They name new listings
 * on purpose. S3, the send that would keep that promise, is parked: the text is intent and not
 * evidence, and that is his decision to make and his to carry.
 *
 * It lives here rather than in resources/lang because the strings are the contract: a state whose
 * sentence can be edited by a translation import is a state whose face can quietly start lying.
 * The unverified sentence in particular exists to be different from the on sentence, and a test
 * holds both to the letter.
 *
 * The Dutch strings and the English button label are literal from FABLE. The remaining English
 * strings are a straight translation and are listed in the STOP as a copy review item, because
 * FABLE pinned only Dutch for those states. The pack asked for the iOS sentence "idem" in English,
 * which is here read as the same instruction in English: an English page that answers in Dutch is
 * not the same sentence, it is the wrong one.
 */

export const VISITOR_PUSH_COPY = {
  nl: {
    // pinned
    turnOn: 'Meldingen aan',
    turnOff: 'Meldingen uit',
    retry: 'Opnieuw proberen',
    // One title per state. Everything except off carries the set A heading, which says what this
    // is without promising anything; off carries the set B heading, which is the invitation.
    title: 'Meldingen van {brand} in deze browser',
    // What stands in for a brand that has no name. A generic word, never another brand's name,
    // and in the language of the sentence it lands in.
    brandFallback: 'deze site',
    // A receipt, not a promise. The worker posts this to the page after showNotification resolved,
    // so this line means the browser was handed a card to draw, and nothing more than that.
    drew: 'Testmelding om {time} door de browser aangenomen: {body} (kaarten={cards})',
    titles: {
      off: 'Meldingen bij nieuw aanbod',
    },
    states: {
      off: 'Zet meldingen aan en we laten het je weten zodra er nieuwe woningen zijn.',
      'turning-on': 'Bezig met aanzetten',
      on: 'Meldingen aan',
      'on-unverified': 'Aan, nog niet bevestigd',
      repair: 'Herstel nodig. Opnieuw proberen',
      // Chrome refuses outright in a private window, and it refuses in a way that looks like a
      // broken button rather than a policy. The sentence names that case first, because somebody
      // testing this is the most likely person to meet it, and then the ordinary way back.
      blocked: 'Je browser blokkeert meldingen voor deze site. In een privévenster kan dit niet. Anders zet je ze aan in de browserinstellingen.',
      // Not a state: the same state with one fact next to it. Somebody who blocked notifications
      // while still on the list gets one live button, and it says "Meldingen uit". Sending them
      // to their browser settings is an answer to a question they are not asking.
      'blocked-subscribed': 'Je browser blokkeert meldingen voor deze site, dus hier komt niets binnen. Je staat nog wel op de lijst. Met de knop hieronder haal je jezelf eraf.',
      unsupported: 'In Safari kunnen meldingen niet direct aan. Zet {brand} eerst op je beginscherm, open het daarna vanaf je beginscherm, en zet meldingen daar aan.',
      'turning-off': 'Bezig met uitzetten',
      'off-cleanup-pending': 'Uitgezet in deze browser. De server bevestigde het nog niet. Opnieuw proberen.',
      // True and not hopeful. This browser is subscribed and this brand is sending nothing, so the
      // sentence promises no delivery, because none is coming, and points at the one button that
      // is on screen next to it.
      excluded: 'Je bent aangemeld. Hier worden op dit moment geen meldingen aangeboden. Je kunt je hier afmelden.',
    },
  },
  en: {
    // turnOn is pinned by FABLE; the rest is translation, the iOS sentence included.
    turnOn: 'Notifications on',
    turnOff: 'Notifications off',
    retry: 'Try again',
    title: 'Notifications from {brand} in this browser',
    brandFallback: 'this site',
    drew: 'Test notification accepted by the browser at {time}: {body} (cards={cards})',
    titles: {
      off: 'New listing alerts',
    },
    states: {
      off: 'Turn on notifications and we will let you know as soon as new homes are available.',
      'turning-on': 'Turning on',
      on: 'Notifications on',
      'on-unverified': 'On, not yet confirmed',
      repair: 'Repair needed. Try again',
      blocked: 'Your browser is blocking notifications for this site. This is not available in a private window. Otherwise allow them in your browser settings.',
      'blocked-subscribed': 'Your browser is blocking notifications for this site, so nothing arrives here. You are still on the list, and the button below takes you off it.',
      unsupported: 'Notifications cannot be turned on directly in Safari. Add {brand} to your home screen first, open it from your home screen, and turn notifications on there.',
      'turning-off': 'Turning off',
      'off-cleanup-pending': 'Turned off in this browser. The server has not confirmed it yet. Try again.',
      excluded: 'You are subscribed. Notifications are not being offered here at the moment. You can unsubscribe here.',
    },
  },
};

export function visitorPushCopyFor(locale) {
  return VISITOR_PUSH_COPY[locale] || VISITOR_PUSH_COPY.nl;
}

/**
 * Fills the brand into a sentence that names one.
 *
 * Four strings named At Home out loud. That is correct for exactly one brand and wrong for every
 * other, and this slice already has a second brand behind the same code. VisitorPushMessage was
 * changed for the same reason on the sending side: a literal there put one brand's name on
 * another brand's lock screen.
 *
 * The fallback keeps the sentence readable rather than leaving a hole, and it is a generic word
 * rather than a brand, because a wrong name is worse than no name.
 */
export function visitorPushBrandIn(sentence, brand, locale, copy) {
  // The fallback used to be one Dutch word for every language, so an English visitor on a brand
  // with no name read "Add deze site to your home screen". Both halves of that sentence are
  // written down; only the word between them was not.
  // The word for a brand with no name comes from the same place as the sentence it lands in.
  // When the page hands the sentences in from the translation table, this has to come from there
  // too, or an English page gets the Dutch word back in an otherwise translated line.
  const fallback = (copy && copy.brandFallback) || visitorPushCopyFor(locale).brandFallback;
  const name = typeof brand === 'string' && brand.trim() !== '' ? brand.trim() : fallback;

  return String(sentence == null ? '' : sentence).split('{brand}').join(name);
}

/** The heading for a state: its own when it has one, the general one when it does not. */
export function visitorPushTitleFor(copy, state) {
  const titles = (copy && copy.titles) || {};

  return titles[state] || (copy && copy.title) || '';
}
